var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { LitElement, html, css, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { localized } from '@lit/localize';
/**
 * Generic two-choice modal. Both buttons carry equal weight (no implicit
 * confirm/cancel asymmetry) — the caller decides what each choice means.
 *
 * Events:
 *   - `primary`   — user picked the primary action
 *   - `secondary` — user picked the secondary action
 *   - `cancel`    — user dismissed (overlay click / Esc), only when
 *                   `dismissible` is true
 */
let ChoiceModal = class ChoiceModal extends LitElement {
    constructor() {
        super(...arguments);
        this.open = false;
        this.title = '';
        this.message = '';
        this.primaryLabel = '';
        this.secondaryLabel = '';
        this.primaryVariant = 'primary';
        this.secondaryVariant = 'secondary';
        this.dismissible = false;
        this._onOverlayClick = (e) => {
            if (e.target === this && this.dismissible) {
                this._dispatch('cancel');
            }
        };
        this._onPrimary = () => this._dispatch('primary');
        this._onSecondary = () => this._dispatch('secondary');
    }
    _dispatch(name) {
        this.dispatchEvent(new CustomEvent(name, { bubbles: true, composed: true }));
    }
    connectedCallback() {
        super.connectedCallback();
        this.addEventListener('click', this._onOverlayClick);
    }
    disconnectedCallback() {
        this.removeEventListener('click', this._onOverlayClick);
        super.disconnectedCallback();
    }
    render() {
        if (!this.open)
            return nothing;
        return html `
      <div class="container" @click=${(e) => e.stopPropagation()}>
        ${this.title ? html `<h2>${this.title}</h2>` : nothing}
        ${this.message ? html `<p>${this.message}</p>` : nothing}
        <div class="button-group">
          <simple-button
            variant=${this.primaryVariant}
            special
            label=${this.primaryLabel}
            @button-click=${this._onPrimary}
          ></simple-button>
          <simple-button
            variant=${this.secondaryVariant}
            label=${this.secondaryLabel}
            @button-click=${this._onSecondary}
          ></simple-button>
        </div>
      </div>
    `;
    }
};
ChoiceModal.styles = css `
    :host {
      display: none;
    }
    :host([open]) {
      display: flex;
      position: fixed;
      inset: 0;
      align-items: center;
      justify-content: center;
      background-color: rgba(0, 0, 0, 0.5);
      z-index: 1000;
    }

    .container {
      background: white;
      padding: 24px 28px;
      border-radius: 10px;
      box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
      text-align: center;
      max-width: 360px;
      width: 90%;
    }

    h2 {
      margin: 0 0 12px 0;
      color: #333;
      font-size: 1.1rem;
    }

    p {
      margin: 0 0 20px 0;
      color: #555;
      line-height: 1.4;
    }

    .button-group {
      display: flex;
      justify-content: center;
      gap: 12px;
      margin-top: 8px;
    }
  `;
__decorate([
    property({ type: Boolean, reflect: true })
], ChoiceModal.prototype, "open", void 0);
__decorate([
    property({ type: String })
], ChoiceModal.prototype, "title", void 0);
__decorate([
    property({ type: String })
], ChoiceModal.prototype, "message", void 0);
__decorate([
    property({ type: String })
], ChoiceModal.prototype, "primaryLabel", void 0);
__decorate([
    property({ type: String })
], ChoiceModal.prototype, "secondaryLabel", void 0);
__decorate([
    property({ type: String })
], ChoiceModal.prototype, "primaryVariant", void 0);
__decorate([
    property({ type: String })
], ChoiceModal.prototype, "secondaryVariant", void 0);
__decorate([
    property({ type: Boolean })
], ChoiceModal.prototype, "dismissible", void 0);
ChoiceModal = __decorate([
    localized(),
    customElement('choice-modal')
], ChoiceModal);
export { ChoiceModal };
//# sourceMappingURL=ChoiceModal.js.map